// "Holding" groups corral people who are on campus but not in the day's program:
// arrivers (until the evening activity) and departers (until their airport
// transfer). Phase 1 identifies them by the name we create them with; a backend
// group "kind" can make this robust later. Only arrivers/departers candidates can
// be routed into them (see the candidate panel), so non-arrivers never get in.
export function holdingGroupKind(group) {
  const name = String(group?.name || "").trim().toLowerCase();
  if (name.startsWith("arrivers")) return "ARRIVERS";
  if (name.startsWith("departers")) return "DEPARTERS";
  return null;
}

export function isHoldingGroup(group) {
  return holdingGroupKind(group) != null;
}

function matchesCandidateCampus(row, selectedCampus) {
  if (!selectedCampus) return true;
  if (selectedCampus === "UNASSIGNED") return !row.campusName;

  return String(row.campusName || "") === String(selectedCampus);
}

function matchesGroupCampus(group, selectedCampus) {
  if (!selectedCampus) return true;
  if (selectedCampus === "UNASSIGNED") return !group.campus;

  return String(group.campus || "") === String(selectedCampus);
}

export function buildSelectedCampusLabel({ selectedCampusName, campusList = [] }) {
  if (!selectedCampusName) return "All campuses";
  if (selectedCampusName === "UNASSIGNED") return "Unassigned";

  const campus = campusList.find(
    (c) => String(c.name) === String(selectedCampusName)
  );

  return campus?.name || String(selectedCampusName);
}

// Slot is stored as free text, so treat it as case-insensitive everywhere it is
// compared. Exported so the same rule can replace the ad-hoc .toUpperCase()
// calls elsewhere rather than adding a sixth spelling of it.
export function normaliseSlot(slot) {
  return String(slot ?? "").trim().toUpperCase();
}

export function buildFilteredActivityGroups({
  groups = [],
  selectedCampusName,
  selectedSlot,
}) {
  return (groups || [])
    .filter((group) => {
      // Holding groups (arrivers/departers) are an all-day concern, so they show
      // on every slot of their day, not just the slot they were created in.
      //
      // Compare case-insensitively. The slot dropdown carries AFTERNOON /
      // EVENING / DAY, but slot is free text on the way in and the API accepts
      // whatever it is given, so a group created outside the UI can hold
      // "afternoon" and vanish from this board while its registers still print.
      // Every other slot comparison in the app already upper-cases first -
      // ActivityCandidatesPanel twice, the rota PDF, activityRegistersPdf - and
      // this was the one that did not.
      const slotMatch =
        isHoldingGroup(group) ||
        !selectedSlot ||
        normaliseSlot(group.slot) === normaliseSlot(selectedSlot);
      const campusMatch = matchesGroupCampus(group, selectedCampusName);

      return slotMatch && campusMatch;
    })
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
}

// productType comes through inconsistently — a string ("EVENT") from the groups
// API but a numeric code ("5") from bookings/products. Treat both as EVENT.
export function isEventProductType(t) {
  const s = String(t ?? "").trim().toUpperCase();
  return s === "EVENT" || s === "5";
}

// Tactical: event products have no slot of their own yet, so a booking can show
// up in the evening candidate list where it doesn't belong. Until events carry a
// slot, treat an event booking in the EVENING slot as "no booked activity" — it
// drops out of the event card and falls into the unbooked candidate group.
function buildEventProductIdSet(products = []) {
  const set = new Set();
  (products || []).forEach((p) => {
    if (isEventProductType(p.productType)) set.add(String(p.productId));
  });
  return set;
}

function isEveningSlot(slot) {
  return String(slot || "").toUpperCase() === "EVENING";
}

function personKey(person) {
  return `${String(person?.personType).toUpperCase()}:${person?.personId}`;
}

// Set of person keys that are already a member of one of the given groups.
function personKeysInGroups(activityGroups = []) {
  const set = new Set();
  (activityGroups || []).forEach((g) => {
    (Array.isArray(g.members) ? g.members : []).forEach((m) => set.add(personKey(m)));
  });
  return set;
}

export function buildUnbookedCandidates({
  candidateRows = [],
  selectedCampusName,
  selectedSlot,
  products = [],
  activityGroups = [],
}) {
  // Candidates are only meaningful for a specific campus (or "Unassigned").
  // With nothing picked ("Select campus…"), show none rather than everyone.
  if (!selectedCampusName) return [];

  const eventProductIds = buildEventProductIdSet(products);
  const evening = isEveningSlot(selectedSlot);
  const countsAsUnbooked = (row) =>
    !row.bookedProductId ||
    (evening && eventProductIds.has(String(row.bookedProductId)));
  // Already placed in a group for this campus+slot — judged by real membership,
  // not just the row's activityGroupId flag. Group membership is what the
  // timetable counts; relying on the flag alone leaves people who were added to a
  // group (e.g. an evening Disco Night, productId null) stuck in the unbooked list.
  const placed = personKeysInGroups(activityGroups);

  return (candidateRows || [])
    .filter((row) => !row.activityGroupId)
    .filter((row) => !row.leadingActivityGroupId)
    .filter((row) => !placed.has(personKey(row)))
    .filter(countsAsUnbooked)
    .filter((row) => matchesCandidateCampus(row, selectedCampusName))
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
}

export function buildBookedCandidateGroups({
  candidateRows = [],
  selectedCampusName,
  products = [],
  activityGroups = [],
  selectedSlot,
}) {
  // See buildUnbookedCandidates — no campus picked means no candidate list.
  if (!selectedCampusName) return [];

  // Tactical: in the evening slot, event bookings are demoted to unbooked (see
  // buildUnbookedCandidates), so they must NOT also form an event candidate card.
  const evening = isEveningSlot(selectedSlot);

  // productId -> productType (needs `productType` on /api/activities/products; until
  // it lands this map is empty and EVENT stickiness simply doesn't kick in).
  const typeByProductId = new Map(
    (products || []).map((p) => [
      String(p.productId),
      String(p.productType || "").toUpperCase(),
    ])
  );
  const isEventProduct = (pid) => isEventProductType(typeByProductId.get(String(pid)));

  // person -> EVERY activity group they're a member of (productId + name), from real
  // group membership (not the row's global activityGroupId). Groups without a linked
  // product (productId null) are still counted — being in one is enough to drop a
  // non-event candidate — but their null productId never matches a booked product, so
  // event stickiness (inGroupForProduct) is unaffected.
  const groupsByPerson = new Map();
  (activityGroups || []).forEach((g) => {
    const productId = g?.productId == null ? null : String(g.productId);
    (Array.isArray(g.members) ? g.members : []).forEach((m) => {
      const key = `${String(m?.personType).toUpperCase()}:${m?.personId}`;
      if (!groupsByPerson.has(key)) groupsByPerson.set(key, []);
      groupsByPerson.get(key).push({ productId, name: g.name || "" });
    });
  });
  const groupsOf = (row) =>
    groupsByPerson.get(`${String(row?.personType).toUpperCase()}:${row?.personId}`) || [];
  const inGroupForProduct = (row) =>
    groupsOf(row).some((g) => g.productId != null && g.productId === String(row.bookedProductId));

  const grouped = new Map();

  (candidateRows || [])
    .filter((row) => !!row.bookedProductId)
    // Evening: event bookings are handled as unbooked, not as an event card.
    .filter((row) => !(evening && isEventProduct(row.bookedProductId)))
    .filter((row) => !row.leadingActivityGroupId)
    .filter((row) => matchesCandidateCampus(row, selectedCampusName))
    .filter((row) => {
      // EVENT candidates are "sticky" — they stay on their product's card until the
      // person is actually in a group LINKED TO THAT product, so a general activity
      // assignment doesn't quietly sweep them off the event list.
      if (isEventProduct(row.bookedProductId)) return !inGroupForProduct(row);
      // Non-event candidates drop off once they're in ANY activity group — judged by
      // the row flag OR real membership. The flag alone misses someone placed in a
      // group for a *different* product, which let non-event candidates linger on
      // their card like event ones (the event stickiness must not bleed to them).
      return !row.activityGroupId && groupsOf(row).length === 0;
    })
    .forEach((row) => {
      const key = `${row.bookedProductId}::${row.bookedProductName || "Unnamed activity"}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          productId: row.bookedProductId,
          productName: row.bookedProductName || "Unnamed activity",
          members: [],
        });
      }

      // Flag candidates who've already been placed in a *different* activity group
      // (e.g. an event candidate who's also landed in a sports group) so the card
      // can highlight them — they need pulling back to the event.
      const otherGroups = groupsOf(row).filter(
        (g) => g.productId !== String(row.bookedProductId)
      );
      grouped.get(key).members.push({
        ...row,
        inOtherGroup: otherGroups.length > 0,
        otherGroupNames: otherGroups.map((g) => g.name).filter(Boolean),
      });
    });

  return Array.from(grouped.values())
    .map((group) => ({
      ...group,
      members: group.members.sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""))
      ),
    }))
    .sort((a, b) => String(a.productName || "").localeCompare(String(b.productName || "")));
}

// `expectedMembersVersion` is the group's membersVersion as loaded; when sent,
// the backend rejects the save (409 STALE_MEMBERS) if the members changed
// since. Omitted → unchecked.
export function buildMemberPayload({
  members = [],
  startDate,
  endDate,
  expectedMembersVersion,
}) {
  const studentIds = [];
  const adultIds = [];

  (members || []).forEach((member) => {
    if (member.personType === "STUDENT") {
      studentIds.push(member.personId);
    } else if (member.personType === "ADULT") {
      adultIds.push(member.personId);
    }
  });

  return {
    startDate,
    endDate: endDate || startDate,
    studentIds,
    adultIds,
    ...(expectedMembersVersion ? { expectedMembersVersion } : {}),
  };
}